import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  createStockTransfer,
  fetchStores,
  selectCreateStockTransferError,
  selectCreateStockTransferStatus,
  selectStores,
  selectStoresError,
  selectStoresStatus,
} from "../features/stockTransfers/stockTransfersSlice";
import "./StockTransferCreatePage.css";

export default function StockTransferCreatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const fromWrapRef = useRef(null);
  const toWrapRef = useRef(null);

  const [fromQuery, setFromQuery] = useState("");
  const [toQuery, setToQuery] = useState("");
  const [fromStoreId, setFromStoreId] = useState(null);
  const [toStoreId, setToStoreId] = useState(null);
  const [fromOpen, setFromOpen] = useState(false);
  const [toOpen, setToOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [formError, setFormError] = useState(null);

  const stores = useSelector(selectStores);
  const storesStatus = useSelector(selectStoresStatus);
  const storesError = useSelector(selectStoresError);
  const createStatus = useSelector(selectCreateStockTransferStatus);
  const createError = useSelector(selectCreateStockTransferError);

  const fromList = useMemo(() => {
    const from = location.state?.from;
    return typeof from === "string" && from.startsWith("/") ? from : null;
  }, [location.state]);

  // Debounced search: fetch stores for currently open dropdown.
  const debounceRef = useRef(null);
  useEffect(() => {
    // IMPORTANT: do NOT mix from/to queries.
    // Otherwise selecting from_store (full label) will poison to_store search.
    const q = fromOpen ? fromQuery : toOpen ? toQuery : "";
    if (!fromOpen && !toOpen) return;
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      dispatch(fetchStores({ search: q }));
    }, 250);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [dispatch, fromOpen, fromQuery, toOpen, toQuery]);

  // Close dropdowns on outside click / Esc
  useEffect(() => {
    if (!fromOpen && !toOpen) return;

    function onMouseDown(e) {
      const t = e.target;
      const inFrom = fromWrapRef.current && fromWrapRef.current.contains(t);
      const inTo = toWrapRef.current && toWrapRef.current.contains(t);
      if (!inFrom) setFromOpen(false);
      if (!inTo) setToOpen(false);
    }

    function onKeyDown(e) {
      if (e.key === "Escape") {
        setFromOpen(false);
        setToOpen(false);
      }
    }

    document.addEventListener("mousedown", onMouseDown, true);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("mousedown", onMouseDown, true);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [fromOpen, toOpen]);

  const visibleStores = useMemo(() => (stores || []).slice(0, 30), [stores]);

  const selectedFromLabel = useMemo(() => {
    if (!fromStoreId) return "";
    const s = (stores || []).find((x) => String(x.id) === String(fromStoreId));
    if (!s) return "";
    return `${s.name || `Store #${s.id}`}${s.address ? ` — ${s.address}` : ""} (#${s.id})`;
  }, [fromStoreId, stores]);

  const selectedToLabel = useMemo(() => {
    if (!toStoreId) return "";
    const s = (stores || []).find((x) => String(x.id) === String(toStoreId));
    if (!s) return "";
    return `${s.name || `Store #${s.id}`}${s.address ? ` — ${s.address}` : ""} (#${s.id})`;
  }, [toStoreId, stores]);

  const canSubmit = useMemo(() => {
    if (!fromStoreId || !toStoreId) return false;
    if (String(fromStoreId) === String(toStoreId)) return false;
    return createStatus !== "saving";
  }, [createStatus, fromStoreId, toStoreId]);

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setFormError(null);
      if (!fromStoreId || !toStoreId) return;
      if (String(fromStoreId) === String(toStoreId)) {
        setFormError("Магазины должны быть разными (from_store != to_store)");
        return;
      }
      try {
        const created = await dispatch(
          createStockTransfer({
            from_store: Number(fromStoreId),
            to_store: Number(toStoreId),
            comment: String(comment || ""),
          })
        ).unwrap();
        navigate(`/warehouse/transfers/${created?.id}`, { replace: true, state: { from: fromList } });
      } catch {
        // error is in redux
      }
    },
    [comment, dispatch, fromList, fromStoreId, navigate, toStoreId]
  );

  return (
    <div className="stock-transfer-create-page">
      <div className="header">
        <div className="breadcrumbs">
          <Link to={fromList || "/warehouse/transfers"} className="link">
            Перемещения по складам
          </Link>
          <span className="sep">/</span>
          <span className="muted">Новое перемещение</span>
        </div>
        <div className="title">Создать перемещение</div>
        <div className="sub-title">
          Создание через <span className="mono">POST /api/product/stock-transfers/</span>.
        </div>
      </div>

      {(createError || storesError || formError) && <div className="error">{formError || createError || storesError}</div>}

      <form onSubmit={onSubmit} className="form">
        <label className="field">
          <div className="label">Откуда (from_store)</div>
          <div className="combo-wrap" ref={fromWrapRef}>
            <input
              value={fromQuery}
              onChange={(e) => {
                setFromQuery(e.target.value);
                setFromStoreId(null);
                setFromOpen(true);
                setToOpen(false);
              }}
              onFocus={() => {
                setFromOpen(true);
                setToOpen(false);
              }}
              placeholder="Начни вводить название/адрес…"
              className="input"
            />
            {fromOpen && (
              <div className="dropdown">
                <div className="dropdown-hint">
                  {storesStatus === "loading"
                    ? "Загрузка…"
                    : selectedFromLabel
                      ? `Выбрано: ${selectedFromLabel}`
                      : "Выбери магазин из списка ниже"}
                </div>
                {visibleStores.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className="dropdown-item"
                    onClick={() => {
                      setFromStoreId(s.id);
                      setFromQuery(`${s.name || `Store #${s.id}`}${s.address ? ` — ${s.address}` : ""}`);
                      setFromOpen(false);
                      setToOpen(false);
                    }}
                  >
                    <div className="dropdown-title">{s.name || `Store #${s.id}`}</div>
                    <div className="dropdown-sub">
                      {s.address || "—"} · #{s.id}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </label>

        <label className="field">
          <div className="label">Куда (to_store)</div>
          <div className="combo-wrap" ref={toWrapRef}>
            <input
              value={toQuery}
              onChange={(e) => {
                setToQuery(e.target.value);
                setToStoreId(null);
                setToOpen(true);
                setFromOpen(false);
              }}
              onFocus={() => {
                setToOpen(true);
                setFromOpen(false);
              }}
              placeholder="Начни вводить название/адрес…"
              className="input"
            />
            {toOpen && (
              <div className="dropdown">
                <div className="dropdown-hint">
                  {storesStatus === "loading"
                    ? "Загрузка…"
                    : selectedToLabel
                      ? `Выбрано: ${selectedToLabel}`
                      : "Выбери магазин из списка ниже"}
                </div>
                {visibleStores.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className="dropdown-item"
                    onClick={() => {
                      setToStoreId(s.id);
                      setToQuery(`${s.name || `Store #${s.id}`}${s.address ? ` — ${s.address}` : ""}`);
                      setToOpen(false);
                      setFromOpen(false);
                    }}
                  >
                    <div className="dropdown-title">{s.name || `Store #${s.id}`}</div>
                    <div className="dropdown-sub">
                      {s.address || "—"} · #{s.id}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </label>

        <label className="field">
          <div className="label">Комментарий (comment)</div>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Комментарий…"
            className="textarea"
            rows={4}
          />
        </label>

        <div className="actions">
          <button type="submit" className="btn-primary" disabled={!canSubmit}>
            {createStatus === "saving" ? "Создаю…" : "Создать"}
          </button>
          <Link to={fromList || "/warehouse/transfers"} className="btn-ghost">
            Отмена
          </Link>
        </div>
      </form>
    </div>
  );
}

